import CustomButton from '@/common/CustomButton';
import Layout from '@/common/Layout';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { cards } from '..';
import CustomTable from '@/common/Table';
import { Button, Input, Tag, Tooltip } from 'antd';
import { columnRenderer, headerRenderer } from 'utilities/helpers';
import { useCallback, useEffect, useState } from 'react';
import CreateUserModal from '../components/CreateUserModal';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers, selectConfigureSlice } from '../redux/slice';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';

const RECORDS_PER_PAGE = 10;

export default function Users() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, users, pagination } = useSelector(selectConfigureSlice);

  const handleGetAllUsers = (
    search = '',
    page = 1,
    per_page = RECORDS_PER_PAGE
  ) => {
    const payload = {
      search,
      page,
      per_page,
      type: 'all'
    };
    dispatch(getUsers(payload));
  };

  useEffect(() => {
    handleGetAllUsers();
  }, []);

  const columns = [
    {
      title: headerRenderer('Name'),
      dataIndex: 'name',
      key: 'name',
      width: '45%',
      render: (text, record) =>
        columnRenderer(
          <div className="flex items-center gap-4">
            <label>{text}</label>
            {!record.is_active ? (
              <Tag color="error" className="!text-[8px]">
                Deactivated
              </Tag>
            ) : null}
          </div>
        )
    },
    {
      title: headerRenderer('Role'),
      dataIndex: 'role',
      key: 'role',
      width: '40%',
      render: (text) => columnRenderer(text)
    },
    {
      title: headerRenderer('Action'),
      dataIndex: 'action',
      key: 'action',
      width: '15%',
      render: (text, record) => (
        <div className="grid grid-cols-4 gap-4 place-items-center">
          <Tooltip title="Edit" overlayClassName="!text-xs">
            <EditOutlined
              onClick={() => setIsModalOpen(record.id)}
              className="text-lg cursor-pointer text-[#0653fa] hover:!scale-105 "
            />
          </Tooltip>
        </div>
      )
    }
  ];

  const dataSource = users.map((user) => {
    return {
      name: user.first_name + ' ' + user.last_name,
      role: user.RoleMaster.role,
      user,
      id: user.id,
      is_active: user.isActive
    };
  });

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleGetAllUsers(value);
    }, 300),
    []
  );

  const handleTableChange = (newPagination) => {
    handleGetAllUsers('', newPagination.current, newPagination.pageSize);
  };

  return (
    <Layout>
      <div className="flex flex-col gap-2 md:gap-6">
        <div className="flex justify-end md:hidden">
          <CustomButton type="outlined" onClick={() => navigate('/configure')}>
            <ArrowLeftOutlined /> Back to Settings
          </CustomButton>
        </div>
        <div className="flex flex-col items-start justify-between md:items-center md:flex-row">
          <div className="flex flex-col gap-1">
            <label className={'!text-xl md:!text-3xl !font-bold md:mt-0 mt-2'}>
              Users
            </label>
            <p className="!text-gray-500 text-balance text-xs md:text-sm !font-semibold">
              {cards[1].description}
            </p>
          </div>

          <div className="items-center hidden gap-4 md:flex">
            <Input
              placeholder="Search user"
              prefix={<SearchOutlined style={{ color: '#00000072' }} />}
              allowClear
              className="!w-52"
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            <CustomButton
              icon={<PlusOutlined />}
              className={'!text-sm rounded-md hidden md:block'}
              type="primary"
              onClick={() => setIsModalOpen(true)}
            >
              Add User
            </CustomButton>
            <CustomButton
              type="outlined"
              onClick={() => navigate('/configure')}
            >
              <ArrowLeftOutlined /> Back to Settings
            </CustomButton>
          </div>
          <div className="flex items-center gap-4 md:hidden">
            <Input
              placeholder="Search user"
              prefix={<SearchOutlined style={{ color: '#00000072' }} />}
              allowClear
              className="!w-auto"
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            <CustomButton
              icon={<PlusOutlined />}
              className={'!text-sm rounded-md block md:hidden'}
              type="primary"
              onClick={() => setIsModalOpen(true)}
            >
              Add User
            </CustomButton>
          </div>
        </div>

        {/* table */}
        <CustomTable
          columns={columns}
          dataSource={dataSource}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          classname={'!min-w-fit'}
          emptyText={'No Users to show'}
        />

        {/* modal */}
        <CreateUserModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          name={'User'}
          onAssign={handleGetAllUsers}
          data={users.find((user) => user.id === isModalOpen)}
        />
      </div>
    </Layout>
  );
}
